interface UtmFields {
  utm_source?: string | null
  utm_medium?: string | null
  utm_campaign?: string | null
  utm_term?: string | null
  utm_content?: string | null
}

interface UtmLabel {
  /** e.g. "meta / paid" */
  primary: string
  /** Short secondary for UI — campaign first, soft-truncated */
  secondary: string | null
  /** Full campaign · content · term for tooltips */
  secondaryFull: string | null
  /** Extra detail parts beyond campaign (content/term) */
  extrasCount: number
  groupKey: string
}

const DEVICE_LABELS: Record<string, string> = {
  '0': 'Desktop',
  '1': 'Mobile',
  '2': 'Tablet',
  desktop: 'Desktop',
  mobile: 'Mobile',
  tablet: 'Tablet',
}

const clean = (value?: string | null): string | null => {
  if (!value) return null
  const trimmed = value.trim()
  if (!trimmed || trimmed.toLowerCase() === 'none') return null
  return trimmed
}

/** Soft-truncate long campaign / UTM strings for dense tables. */
export function softTruncate(value: string, max = 40): string {
  if (value.length <= max) return value
  return `${value.slice(0, Math.max(1, max - 1)).trimEnd()}…`
}

/** Map GA/bridge device codes (`0`/`1`/`2`) to human labels. */
export function formatDeviceLabel(raw?: string | null): string {
  if (raw == null || raw === '') return '(unknown)'
  const key = String(raw).trim()
  return DEVICE_LABELS[key] ?? DEVICE_LABELS[key.toLowerCase()] ?? key
}

export function formatUtmLabel(utm: UtmFields): UtmLabel {
  const source = clean(utm.utm_source)
  const medium = clean(utm.utm_medium)
  const campaign = clean(utm.utm_campaign)
  const content = clean(utm.utm_content)
  const term = clean(utm.utm_term)

  const primary = source
    ? medium
      ? `${source} / ${medium}`
      : source
    : 'Direct'

  const detailParts = [campaign, content, term].filter(Boolean) as string[]
  const secondaryFull = detailParts.length > 0 ? detailParts.join(' · ') : null
  const extrasCount = Math.max(0, detailParts.length - (campaign ? 1 : 0))

  // Prefer campaign as the readable secondary; tuck content/term behind +N
  let secondary: string | null = null
  if (campaign) {
    const campaignLabel = softTruncate(campaign, 36)
    secondary =
      extrasCount > 0 ? `${campaignLabel} · +${extrasCount}` : campaignLabel
  } else if (secondaryFull) {
    secondary = softTruncate(secondaryFull, 40)
  }

  const groupKey = [
    source ?? 'direct',
    medium ?? '-',
    campaign ?? '-',
    content ?? '-',
    term ?? '-',
  ].join('|')

  return { primary, secondary, secondaryFull, extrasCount, groupKey }
}
